import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  ResponsiveContent,
  ResponsiveFooter,
} from '../ui/ResponsiveLayout';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';
import { FinishFlowAction } from '../../flows/FinishFlowAction';
import { Card, Button } from '../ui/';
import { DocumentsIcon } from '../ui/Icons';
import { documentService } from '../../services/documentService';
import type { AppDocument } from '../../types/document';

interface Property {
  id: string;
  type: 'house' | 'apartment' | 'land' | 'commercial' | 'other';
  address: string;
  currentValue: number;
  mortgageBalance: number;
  notes?: string;
  documents?: AppDocument[];
}

interface TaxDocument {
  id: string;
  type: 'federal' | 'state' | 'property' | 'other';
  year: string;
  description: string;
  status: 'filed' | 'pending' | 'extension';
  amount?: number;
  documents: AppDocument[];
  notes?: string;
}

interface IncomeSource {
  id: string;
  type: string;
  sourceName: string;
  payerInstitution: string;
  frequency: string;
  approximateAmount?: number;
  notes?: string;
  documents?: AppDocument[];
}

/** Backup / session-derived rows merged for inventory summaries (financial, property, tax). */
interface FinancialAnswersRow {
  id?: string;
  name?: string;
  type?: string;
  institution?: string;
  payerInstitution?: string;
  address?: string;
  description?: string;
  accountNumber?: string;
  documents?: unknown;
  account?: string;
  year?: string | number;
  status?: string;
  sourceName?: string;
  frequency?: string;
  approximateAmount?: number;
  notes?: string;
  currentValue?: number;
  mortgageBalance?: number;
}

type DocumentSection =
  | 'cashAccounts'
  | 'investmentAccounts'
  | 'retirementAccounts'
  | 'otherFinancialAssets'
  | 'incomeSources'
  | 'properties'
  | 'documents';

const normalizeDocuments = (docs: unknown): AppDocument[] => {
  return documentService.normalizeMany(docs);
};

const Frame213: React.FC = () => {
  const navigate = useNavigate();
  const { session, updateTaskProgress } = useSessionPersistence();

  const openDocument = (doc: AppDocument) => {
    if (!doc.id) return;
    documentService.open(doc.id);
  };

  const downloadDocument = async (doc: AppDocument) => {
    if (!doc.id) return;

    try {
      await documentService.download(doc.id, doc.name);
    } catch (error) {
      console.error('[Frame213] Failed to download document:', error);
      alert('Failed to download document.');
    }
  };

  const loadCashAccounts = () => {
    const answers = session.taskProgress?.financial_assets?.answers;

    if (answers?.cashAccounts?.length > 0) {
      return answers.cashAccounts.map((account: FinancialAnswersRow) => ({
        ...account,
        documents: normalizeDocuments(account.documents || []),
      }));
    }

    try {
      const backup = localStorage.getItem('cash-accounts-backup');
      if (backup) {
        const accounts = JSON.parse(backup);
        if (accounts.length > 0) {
          return accounts.map((account: FinancialAnswersRow) => ({
            ...account,
            documents: normalizeDocuments(account.documents || []),
          }));
        }
      }
    } catch (error) {
      console.error('[Frame213] Failed to load cash accounts from backup:', error);
    }

    return [];
  };

  const loadInvestmentAccounts = () => {
    const answers = session.taskProgress?.financial_assets?.answers;

    if (answers?.investmentAccounts?.length > 0) {
      return answers.investmentAccounts.map((account: FinancialAnswersRow) => ({
        ...account,
        documents: normalizeDocuments(account.documents || []),
      }));
    }

    try {
      const backup = localStorage.getItem('investment-accounts-backup');
      if (backup) {
        const accounts = JSON.parse(backup);
        if (accounts.length > 0) {
          return accounts.map((account: FinancialAnswersRow) => ({
            ...account,
            documents: normalizeDocuments(account.documents || []),
          }));
        }
      }
    } catch (error) {
      console.error('[Frame213] Failed to load investment accounts from backup:', error);
    }

    return [];
  };

  const loadRetirementAccounts = () => {
    const answers = session.taskProgress?.financial_assets?.answers;

    if (answers?.retirementAccounts?.length > 0) {
      return answers.retirementAccounts.map((account: FinancialAnswersRow) => ({
        ...account,
        documents: normalizeDocuments(account.documents || []),
      }));
    }

    try {
      const backup = localStorage.getItem('retirement-accounts-backup');
      if (backup) {
        const accounts = JSON.parse(backup);
        if (accounts.length > 0) {
          return accounts.map((account: FinancialAnswersRow) => ({
            ...account,
            documents: normalizeDocuments(account.documents || []),
          }));
        }
      }
    } catch (error) {
      console.error('[Frame213] Failed to load retirement accounts from backup:', error);
    }

    return [];
  };

  const loadOtherFinancialAssets = () => {
    const answers = session.taskProgress?.financial_assets?.answers;

    if (answers?.otherFinancialAssets?.length > 0) {
      return answers.otherFinancialAssets.map((asset: FinancialAnswersRow) => ({
        ...asset,
        documents: normalizeDocuments(asset.documents || []),
      }));
    }

    try {
      const backup = localStorage.getItem('other-financial-assets-backup');
      if (backup) {
        const assets = JSON.parse(backup);
        if (assets.length > 0) {
          return assets.map((asset: FinancialAnswersRow) => ({
            ...asset,
            documents: normalizeDocuments(asset.documents || []),
          }));
        }
      }
    } catch (error) {
      console.error('[Frame213] Failed to load other financial assets from backup:', error);
    }

    return [];
  };

  const loadIncomeSources = (): IncomeSource[] => {
    const answers = session.taskProgress?.financial_assets?.answers;

    if (answers?.incomeSources?.length > 0) {
      return answers.incomeSources.map((source: IncomeSource) => ({
        ...source,
        documents: normalizeDocuments(source.documents || []),
      }));
    }

    try {
      const backup = localStorage.getItem('income-sources-backup');
      if (backup) {
        const sources = JSON.parse(backup);
        if (sources.length > 0) {
          return sources.map((source: IncomeSource) => ({
            ...source,
            documents: normalizeDocuments(source.documents || []),
          }));
        }
      }
    } catch (error) {
      console.error('[Frame213] Failed to load income sources from backup:', error);
    }

    return [];
  };

  const loadProperties = () => {
    const props = session.taskProgress?.properties?.answers?.properties || [];

    if (props.length > 0) {
      return props.map((property: FinancialAnswersRow) => ({
        ...property,
        documents: normalizeDocuments(property.documents || []),
      }));
    }

    try {
      const backup = localStorage.getItem('properties-backup');
      if (backup) {
        const backupProps = JSON.parse(backup);
        if (backupProps.length > 0) {
          return backupProps.map((property: FinancialAnswersRow) => ({
            ...property,
            documents: normalizeDocuments(property.documents || []),
          }));
        }
      }
    } catch (error) {
      console.error('[Frame213] Failed to load properties from backup:', error);
    }

    return [];
  };

  const loadTaxDocuments = () => {
    const docs = session.taskProgress?.tax_documents?.answers?.documents || [];

    if (docs.length > 0) {
      return docs.map((doc: FinancialAnswersRow) => ({
        ...doc,
        documents: normalizeDocuments(doc.documents || []),
      }));
    }

    try {
      const backup = localStorage.getItem('tax-documents-backup');
      if (backup) {
        const backupDocs = JSON.parse(backup);
        if (backupDocs.length > 0) {
          return backupDocs.map((doc: FinancialAnswersRow) => ({
            ...doc,
            documents: normalizeDocuments(doc.documents || []),
          }));
        }
      }
    } catch (error) {
      console.error('[Frame213] Failed to load tax documents from backup:', error);
    }

    return [];
  };

  const removeDocumentFromSection = async (
    section: DocumentSection,
    itemId: string,
    documentIndex: number
  ) => {
    const financialAnswers = session.taskProgress?.financial_assets?.answers || {};
    const propertyAnswers = session.taskProgress?.properties?.answers || {};
    const taxAnswers = session.taskProgress?.tax_documents?.answers || {};

    if (section === 'properties') {
      const currentProperties =
        propertyAnswers.properties?.length > 0
          ? propertyAnswers.properties.map((item: FinancialAnswersRow) => ({
              ...item,
              documents: normalizeDocuments(item.documents || []),
            }))
          : loadProperties();

      const property = currentProperties.find((item: FinancialAnswersRow) => item.id === itemId);
      const docToRemove = property?.documents?.[documentIndex];

      if (docToRemove?.id) {
        try {
          await documentService.delete(docToRemove.id);
        } catch (error) {
          console.error('[Frame213] Failed deleting property document:', error);
          alert('Failed to delete document.');
          return;
        }
      }

      const updatedProperties = currentProperties.map((item: FinancialAnswersRow) => {
        if (item.id !== itemId) return item;

        return {
          ...item,
          documents: normalizeDocuments(item.documents || []).filter(
            (_row: AppDocument, index: number) => index !== documentIndex
          ),
        };
      });

      try {
        localStorage.setItem('properties-backup', JSON.stringify(updatedProperties));
        localStorage.setItem(
          'properties-data-backup',
          JSON.stringify({
            properties: updatedProperties,
            lastUpdated: new Date().toISOString(),
            taskId: 'properties',
          })
        );
      } catch (error) {
        console.error('[Frame213] Failed saving updated properties backup:', error);
      }

      await updateTaskProgress('properties', { properties: updatedProperties });
      window.location.reload();
      return;
    }

    if (section === 'documents') {
      const currentDocs =
        taxAnswers.documents?.length > 0
          ? taxAnswers.documents.map((item: FinancialAnswersRow) => ({
              ...item,
              documents: normalizeDocuments(item.documents || []),
            }))
          : loadTaxDocuments();

      const taxDoc = currentDocs.find((item: FinancialAnswersRow) => item.id === itemId);
      const docToRemove = taxDoc?.documents?.[documentIndex];

      if (docToRemove?.id) {
        try {
          await documentService.delete(docToRemove.id);
        } catch (error) {
          console.error('[Frame213] Failed deleting tax document attachment:', error);
          alert('Failed to delete document.');
          return;
        }
      }

      const updatedDocs = currentDocs.map((item: FinancialAnswersRow) => {
        if (item.id !== itemId) return item;

        return {
          ...item,
          documents: normalizeDocuments(item.documents || []).filter(
            (_row: AppDocument, index: number) => index !== documentIndex
          ),
        };
      });

      try {
        localStorage.setItem('tax-documents-backup', JSON.stringify(updatedDocs));
      } catch (error) {
        console.error('[Frame213] Failed saving updated tax docs backup:', error);
      }

      await updateTaskProgress('tax_documents', { documents: updatedDocs }, 'in-progress');
      window.location.reload();
      return;
    }

    const currentItems =
      financialAnswers?.[section]?.length > 0
        ? financialAnswers[section].map((item: FinancialAnswersRow) => ({
            ...item,
            documents: normalizeDocuments(item.documents || []),
          }))
        : section === 'cashAccounts'
          ? loadCashAccounts()
          : section === 'investmentAccounts'
            ? loadInvestmentAccounts()
            : section === 'retirementAccounts'
              ? loadRetirementAccounts()
              : section === 'otherFinancialAssets'
                ? loadOtherFinancialAssets()
                : loadIncomeSources();

    const item = currentItems.find((entry: FinancialAnswersRow) => entry.id === itemId);
    const docToRemove = item?.documents?.[documentIndex];

    if (docToRemove?.id) {
      try {
        await documentService.delete(docToRemove.id);
      } catch (error) {
        console.error('[Frame213] Failed deleting financial asset document:', error);
        alert('Failed to delete document.');
        return;
      }
    }

    const updatedItems = currentItems.map((item: FinancialAnswersRow) => {
      if (item.id !== itemId) return item;

      return {
        ...item,
        documents: normalizeDocuments(item.documents || []).filter(
          (_row: AppDocument, index: number) => index !== documentIndex
        ),
      };
    });

    const backupKeyMap: Record<string, string> = {
      cashAccounts: 'cash-accounts-backup',
      investmentAccounts: 'investment-accounts-backup',
      retirementAccounts: 'retirement-accounts-backup',
      otherFinancialAssets: 'other-financial-assets-backup',
      incomeSources: 'income-sources-backup',
    };

    try {
      const backupKey = backupKeyMap[section];
      if (backupKey) {
        localStorage.setItem(backupKey, JSON.stringify(updatedItems));
      }
    } catch (error) {
      console.error('[Frame213] Failed saving updated financial assets backup:', error);
    }

    await updateTaskProgress('financial_assets', {
      ...financialAnswers,
      [section]: updatedItems,
    });

    window.location.reload();
  };

  const renderDocuments = (
    docs: AppDocument[] = [],
    section: DocumentSection,
    itemId: string
  ) => {
    const normalizedDocs = normalizeDocuments(docs);
    if (!normalizedDocs || normalizedDocs.length === 0) return null;

    return (
      <div className="mt-3 space-y-2">
        {normalizedDocs.map((doc, index) => (
          <div
            key={`${itemId}-doc-${doc.id || index}-${index}`}
            className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-md border border-stone-200 bg-white px-3 py-2"
          >
            <button
              type="button"
              onClick={() => openDocument(doc)}
              className="flex items-start gap-2 min-w-0 w-full sm:flex-1 text-left"
            >
              <DocumentsIcon className="w-4 h-4 text-cyan-700 flex-shrink-0 mt-0.5" />
              <span
                className="text-sm font-medium text-stone-800 truncate min-w-0"
                title={doc.name}
              >
                {doc.name}
              </span>
            </button>

            <div className="flex flex-wrap items-center gap-2 sm:shrink-0">
              <button
                type="button"
                onClick={() => downloadDocument(doc)}
                className="text-cyan-600 hover:text-cyan-800 text-xs font-medium"
                title="Download document"
              >
                Download
              </button>

              <button
                type="button"
                onClick={() => removeDocumentFromSection(section, itemId, index)}
                className="text-red-500 hover:text-red-700 text-xs shrink-0"
                title="Remove document"
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const cashAccounts = loadCashAccounts();
  const investmentAccounts = loadInvestmentAccounts();
  const retirementAccounts = loadRetirementAccounts();
  const otherFinancialAssets = loadOtherFinancialAssets();
  const incomeSources = loadIncomeSources();
  const properties: Property[] = loadProperties();
  const taxDocuments: TaxDocument[] = loadTaxDocuments();

  const groupAccountsByInstitution = <T extends FinancialAnswersRow>(accounts: T[]) => {
    return accounts.reduce<Record<string, T[]>>((acc, account) => {
      const institution =
        account.institution ||
        account.payerInstitution ||
        account.address ||
        account.type ||
        account.description ||
        'Unknown Institution';

      if (!acc[institution]) {
        acc[institution] = [];
      }

      acc[institution].push(account);
      return acc;
    }, {});
  };

  const formatType = (value?: string) => {
    if (!value) return 'N/A';
    return value.replace(/_/g, ' ');
  };

  const formatFrequency = (value?: string) => {
    if (!value) return 'N/A';
    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const handleEditSection = (section: string) => {
    switch (section) {
      case 'cash':
        navigate('/frame-186');
        break;
      case 'investment':
        navigate('/frame-187');
        break;
      case 'retirement':
        navigate('/frame-188');
        break;
      case 'other_financial':
        navigate('/frame-189');
        break;
      case 'income_sources':
        navigate('/frame-191');
        break;
      case 'property':
        navigate('/frame-190');
        break;
      case 'tax':
        navigate('/frame-239');
        break;
      default:
        break;
    }
  };

  return (
    <ResponsiveContainer background="stone">
      <ResponsiveContent>
        <div className="w-full space-y-6">
          <h1 className="text-cyan-900 text-3xl font-semibold font-['Test_Domaine_Display'] mb-8">
            Inventory of Assets Summary
          </h1>

          <Card className="w-full p-6">
            <h3 className="text-lg font-semibold text-stone-800 mb-6">Assets Overview</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-900">
                  {cashAccounts.length + investmentAccounts.length + retirementAccounts.length}
                </div>
                <div className="text-sm text-stone-500 mt-1">Total Accounts</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{properties.length}</div>
                <div className="text-sm text-stone-500 mt-1">Properties</div>
              </div>

              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">{taxDocuments.length}</div>
                <div className="text-sm text-stone-500 mt-1">Tax Returns</div>
              </div>
            </div>
          </Card>

          <Card className="w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Cash and Deposit Accounts
              </h3>
              <Button variant="outline" size="sm" onClick={() => handleEditSection('cash')}>
                Edit
              </Button>
            </div>

            {cashAccounts.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(cashAccounts)).map(
                  ([inst, accs]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {accs.map((acc) => (
                        <div key={acc.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">{acc.name}</div>
                          <div className="text-sm text-stone-600">
                            Type: {formatType(acc.type)} • Ending:{' '}
                            {acc.accountNumber?.slice(-4) || 'N/A'}
                          </div>
                          {renderDocuments(acc.documents, 'cashAccounts', acc.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm mb-2">No cash accounts added yet.</p>
                <Button variant="outline" onClick={() => handleEditSection('cash')}>
                  Add Account
                </Button>
              </div>
            )}
          </Card>

          <Card className="w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Investment Accounts
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEditSection('investment')}
              >
                Edit
              </Button>
            </div>

            {investmentAccounts.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(investmentAccounts)).map(
                  ([inst, accs]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {accs.map((acc) => (
                        <div key={acc.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">{acc.name}</div>
                          <div className="text-sm text-stone-600">
                            Type: {formatType(acc.type)} • Ending:{' '}
                            {acc.accountNumber?.slice(-4) || 'N/A'}
                          </div>
                          {renderDocuments(acc.documents, 'investmentAccounts', acc.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm mb-2">
                  No investment accounts added yet.
                </p>
                <Button variant="outline" onClick={() => handleEditSection('investment')}>
                  Add Account
                </Button>
              </div>
            )}
          </Card>

          <Card className="w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Retirement Accounts
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEditSection('retirement')}
              >
                Edit
              </Button>
            </div>

            {retirementAccounts.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(retirementAccounts)).map(
                  ([inst, accs]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {accs.map((acc) => (
                        <div key={acc.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">{acc.name}</div>
                          <div className="text-sm text-stone-600">
                            Type: {formatType(acc.type)} • Ending:{' '}
                            {acc.accountNumber?.slice(-4) || 'N/A'}
                          </div>
                          {renderDocuments(acc.documents, 'retirementAccounts', acc.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm mb-2">
                  No retirement accounts added yet.
                </p>
                <Button variant="outline" onClick={() => handleEditSection('retirement')}>
                  Add Account
                </Button>
              </div>
            )}
          </Card>

          <Card className="w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-stone-800">
                Other Financial Assets
              </h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEditSection('other_financial')}
              >
                Edit
              </Button>
            </div>

            {otherFinancialAssets.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(otherFinancialAssets)).map(
                  ([inst, accs]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {accs.map((acc) => (
                        <div key={acc.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">{acc.name}</div>
                          <div className="text-sm text-stone-600">
                            Type: {formatType(acc.type)} • Ending:{' '}
                            {acc.accountNumber?.slice(-4) || 'N/A'}
                          </div>
                          {renderDocuments(acc.documents, 'otherFinancialAssets', acc.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm mb-2">
                  No other financial assets added yet.
                </p>
                <Button
                  variant="outline"
                  onClick={() => handleEditSection('other_financial')}
                >
                  Add Asset
                </Button>
              </div>
            )}
          </Card>

          <Card className="w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-stone-800">Income Sources</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEditSection('income_sources')}
              >
                Edit
              </Button>
            </div>

            {incomeSources.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(incomeSources)).map(
                  ([inst, sources]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {sources.map((source: IncomeSource) => (
                        <div key={source.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">
                            {source.sourceName}
                          </div>

                          <div className="text-sm text-stone-600">
                            Type: {formatType(source.type)} • Frequency:{' '}
                            {formatFrequency(source.frequency)}
                            {typeof source.approximateAmount === 'number' &&
                              ` • Approx. Amount: $${source.approximateAmount.toLocaleString()}`}
                          </div>

                          {source.notes && (
                            <div className="text-sm text-stone-600 mt-2">
                              {source.notes}
                            </div>
                          )}

                          {renderDocuments(source.documents, 'incomeSources', source.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm mb-2">
                  No income sources added yet.
                </p>
                <Button
                  variant="outline"
                  onClick={() => handleEditSection('income_sources')}
                >
                  Add Income Source
                </Button>
              </div>
            )}
          </Card>

          <Card className="w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-stone-800">Properties</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleEditSection('property')}
              >
                Edit
              </Button>
            </div>

            {properties.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(properties)).map(
                  ([inst, accs]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {accs.map((acc) => (
                        <div key={acc.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">{acc.address}</div>
                          <div className="text-sm text-stone-600">Type: {acc.type}</div>
                          {renderDocuments(acc.documents, 'properties', acc.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm mb-2">No properties added yet.</p>
                <Button variant="outline" onClick={() => handleEditSection('property')}>
                  Add Property
                </Button>
              </div>
            )}
          </Card>

          <Card className="w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-stone-800">Tax Returns</h3>
              <Button variant="outline" size="sm" onClick={() => handleEditSection('tax')}>
                Edit
              </Button>
            </div>

            {taxDocuments.length > 0 ? (
              <div className="max-h-[300px] overflow-y-auto pr-2 custom-scrollbar space-y-6">
                {Object.entries(groupAccountsByInstitution(taxDocuments)).map(
                  ([inst, accs]) => (
                    <div key={inst} className="space-y-3">
                      <h4 className="text-xs font-bold text-stone-400 uppercase tracking-wider border-b border-stone-100 pb-1">
                        {inst}
                      </h4>

                      {accs.map((acc) => (
                        <div key={acc.id} className="bg-stone-50 rounded-lg p-4">
                          <div className="font-medium text-stone-800">
                            {acc.description} ({acc.year})
                          </div>
                          <div className="text-sm text-stone-600">
                            Status: {acc.status}
                          </div>
                          {renderDocuments(acc.documents, 'documents', acc.id)}
                        </div>
                      ))}
                    </div>
                  )
                )}
              </div>
            ) : (
              <div className="text-center py-6">
                <p className="text-stone-500 text-sm mb-2">No tax returns added yet.</p>
                <Button variant="outline" onClick={() => handleEditSection('tax')}>
                  Add Return
                </Button>
              </div>
            )}
          </Card>
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Inventory Your Assets"
        subtitle="Review and confirm your assets"
        progressPercent={100}
        onBack={() => navigate('/frame-239')}
        backText="Back"
        nextNode={
          <FinishFlowAction
            flowId="inventory"
            onFinished={() => navigate('/frame-140')}
            buttonText="Continue to Dashboard"
            className="!bg-cyan-700 hover:!bg-cyan-800 rounded-sm"
          />
        }
      />
    </ResponsiveContainer>
  );
};

export default Frame213;